import React, { useState } from "react";
import {
  Apple,
  BarChart3,
  Users,
  MapPin,
  Globe,
  Languages,
  Target,
  Framer,
  MessageCircleHeart,
} from "lucide-react";
import DemographicCard from "./DemographicCard";
import { Tooltip } from "./Tooltip";
import { WorldMap } from "./Base/WorldMap";

export const InfluencerAudienceData = ({ userDatas }) => {
  const [showAll] = useState(false);
  const [audience] = useState("followers");
  const itemsToShow = showAll
    ? userDatas?.audience_followers_brand_affinity
    : userDatas?.audience_followers_brand_affinity?.slice(0, 4);

  const locationShow = showAll
    ? userDatas?.audience_followers_geo_cities
    : userDatas?.audience_followers_geo_cities?.slice(0, 5);

  const [showInterest] = useState(false);
  const brandAffinityInterest = showInterest
    ? userDatas?.audience_followers_interests
    : userDatas?.audience_followers_interests?.slice(0, 4);

  const femaleWeight = userDatas?.audience_followers_genders?.[0]?.weight || 0;
  const maleWeight = userDatas?.audience_followers_genders?.[1]?.weight || 0;
  const malePercentage = (maleWeight * 100)?.toFixed(2) || "0.00";
  const femalePercentage = (femaleWeight * 100)?.toFixed(2) || "0.00";

  // Calculate stroke-dasharray for donut chart
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const maleStroke = (malePercentage / 100) * circumference;
  const femaleStroke = (femalePercentage / 100) * circumference;

  function getFlag(key) {
    switch (key) {
      case "CA":
        return "🇨🇦";
      case "GB":
        return "🇬🇧";
      case "IN":
        return "🇮🇳";
      case "US":
        return "🇺🇸";
      default:
        return "🏳️";
    }
  }

  const audienceData =
    userDatas?.audience_followers_reachability?.map((value) => {
      return {
        range: value.code,
        percentage: (value.weight * 100).toFixed(2),
      };
    }) || [];
  const audienceDataDisplay = [
    { range: "<500" },
    { range: "500-1k" },
    { range: "1-5k" },
    { range: ">15k" },
  ];

  const followersData =
    userDatas?.audience_followers_notable_users?.slice(0, 16) || [];

  const ageGenderData =
    userDatas?.audience_followers_genders_per_age?.map((item) => ({
      age: item.code,
      male: item.male * 100 || 0,
      female: item.female * 100 || 0,
    })) || [];

  return (
    <div className="space-y-6 mb-8 px-4 sm:px-6 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-8 w-full">
        <div className="space-y-2 w-full">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Audience Analytics
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            Comprehensive insights about your audience demographics
          </p>
        </div>

        <div className="space-y-3">
          <div className=" w-full sm:w-auto inline-flex ">
            <button
              className={`!px-6 !py-6 !mr-2 !rounded-2xl !font-bold !transition-all duration-300 !flex !items-center !gap-2 !flex-1 !sm:flex-initial !justify-center !group ${
                audience === "followers"
                  ? "!bg-gradient-to-br !from-green-500 !via-green-600 !to-emerald-600 !text-white shadow-2xl !shadow-green-500/40 "
                  : "!text-gray-700 !bg-white hover:!shadow-lg border border-green-200 "
              }`}
            >
              <span className="text-base">Followers</span>
            </button>
            <button
              className={`!px-6 !py-6 !rounded-2xl !font-bold !transition-all duration-300 !flex !items-center !gap-2 !flex-1 !sm:flex-initial !justify-center !group ${
                audience === "likers"
                  ? "!bg-gradient-to-br !from-green-500 !via-green-600 !to-emerald-600 !text-white shadow-2xl !shadow-green-500/40 "
                  : "!text-gray-700 !bg-white hover:!shadow-lg  border border-green-200 "
              }`}
            >
              <span className="text-base">Likers</span>
            </button>
          </div>
        </div>
      </div>

      {/* Top Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6 w-full">
        {/* Audience Info */}
        <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 md:col-span-2 xl:col-span-1">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Audience Data
              </h3>
              <p className="text-sm text-green-600 font-medium">
                Complete Insights
              </p>
            </div>
          </div>
          <div className="mt-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              Audience data includes insights from the influencer's{" "}
              <strong className="text-green-700">entire follower base</strong> —
              not limited with the data of new followers in the last 7 or 30
              days like Instagram shows.
            </p>
          </div>
        </div>

        {/* Gender Split */}
        <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              Gender Split
              <Tooltip
                description={
                  "We determine historical followers gender by analyzing the profile picture, name, text in the profile description (bio) and selfies in recent posts."
                }
              />
            </h3>
          </div>

          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <svg width="120" height="120" className="transform -rotate-90">
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                />
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="#F59E0B"
                  strokeWidth="12"
                  strokeDasharray={`${maleStroke} ${circumference}`}
                  strokeLinecap="round"
                />
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="12"
                  strokeDasharray={`${femaleStroke} ${circumference}`}
                  strokeDashoffset={-maleStroke}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between ">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-[#F59E0B] rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Male</span>
              </div>
              <span className="text-sm font-bold text-green-700">
                {malePercentage}%
              </span>
            </div>
            <div className="flex items-center justify-between ">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-[#3B82F6] rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">
                  Female
                </span>
              </div>
              <span className="text-sm font-bold text-green-700">
                {femalePercentage}%
              </span>
            </div>
          </div>
        </div>

        {/* Age and Gender Chart */}
        <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 md:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              Age & Gender Distribution
              <Tooltip
                description={
                  "We determine historical followers age by analyzing the profile picture, text in the profile description (bio) and selfies in recent posts."
                }
              />
            </h3>
          </div>
          <div className="flex items-end justify-between h-48 space-x-1 sm:space-x-2">
            {ageGenderData?.map((group, idx) => (
              <div
                key={group.age}
                className="flex flex-col items-center flex-1"
              >
                <div className="flex items-end space-x-1 w-full justify-center">
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-green-700 font-medium mb-1">
                      {group?.male?.toFixed(2) || "0.0"}%
                    </span>
                    <div
                      className="w-3 sm:w-4 bg-green-700 rounded-t-lg transition-all duration-500 hover:bg-green-800"
                      style={{
                        height: `${((group.male || 0) / 100) * 350}px`,
                        minHeight: "4px",
                      }}
                    ></div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-green-500 font-medium mb-1">
                      {group?.female?.toFixed(1) || "0.0"}%
                    </span>
                    <div
                      className="w-3 sm:w-4 bg-green-400 rounded-t-lg transition-all duration-500 hover:bg-green-500"
                      style={{
                        height: `${((group.female || 0) / 100) * 350}px`,
                        minHeight: "4px",
                      }}
                    ></div>
                  </div>
                </div>
                <div className="text-xs sm:text-sm font-medium text-gray-700 mt-3 text-center">
                  {group.age}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-200 mb-6 w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex gap-2">
              Geographic Distribution{" "}
              <Tooltip
                description={
                  "We determine historical followers location by analyzing location tags, language and caption of the recent posts."
                }
              />
            </h3>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-6 w-full">
          {/* World Map Visualization */}
          <div className="w-full lg:w-1/2 relative bg-gradient-to-br rounded-2xl">
            <WorldMap points={locationShow || []} />
          </div>

          {/* Location List */}
          <div className="w-full lg:w-1/2 space-y-3">
            {locationShow?.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 "
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <span className="text-xl sm:text-2xl">
                    {getFlag(location.country_code)}
                  </span>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">
                      {location.name}
                    </div>
                    {/* <div className="text-xs sm:text-sm text-gray-500">
                        {location.country_name}
                      </div> */}
                  </div>
                </div>
                <div className="text-right">
                  <div className=" text-gray-500">
                    {(location.weight * 100)?.toFixed(2) || "0.00"}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Demographic Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 w-full">
        <DemographicCard
          title="Location by country"
          items={userDatas?.audience_followers_geo_countries}
          description={""}
          icon={<Globe className="w-5 h-5 text-green-600" />}
        />
        <DemographicCard
          title="Ethnicity"
          items={userDatas?.audience_followers_ethnicities}
          description={
            "We determine audience ethnicity by analyzing the profile picture, text in the profile description (bio) and selfies in recent posts."
          }
          icon={<Users className="w-5 h-5 text-green-600" />}
        />
        <DemographicCard
          title="Language"
          items={userDatas?.audience_followers_languages}
          description={
            "We determine historical followers language by analyzing caption of the recent posts."
          }
          icon={<Languages className="w-5 h-5 text-green-600" />}
        />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 w-full">
        {/* Influencer Percentage */}
        <div className=" bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 text-center flex flex-col items-center justify-center text-green-700">
          <div className="text-3xl sm:text-4xl font-bold mb-2">5.92%</div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-green-600 text-sm font-medium">
              Influencer Ratio
            </span>
            <Tooltip
              description={
                "Shows what percentage of followers are influencers. Notable accounts for a 0.99 quantile can largely differ from 1% to 20%."
              }
            />
          </div>
          <div className="mt-3 text-green-600 text-xs">
            High-quality audience engagement
          </div>
        </div>

        {/* Audience Reachability */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Audience Reachability
            </h3>
            <Tooltip
              description={
                "Shows percentage of followers that follow more than 1500 accounts, between 1000-1500, 500-1000 and below 500."
              }
            />
          </div>

          <div className="flex items-end justify-between h-40 sm:h-48 mb-6 space-x-1 sm:space-x-2">
            {audienceData?.map((item, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div
                  className="relative w-6 sm:w-8 rounded-2xl overflow-hidden bg-gray-100 transition-all duration-500 hover:shadow-lg"
                  style={{ height: "120px" }}
                >
                  <div
                    className="absolute bottom-0 left-0 w-full bg-gray-400 rounded-2xl transition-all duration-500"
                    style={{ height: `${item.percentage}%` }}
                  ></div>
                </div>
                <div className="text-xs sm:text-sm font-bold text-gray-400 mt-2">
                  {item.percentage}%
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            {audienceDataDisplay?.map((item, index) => (
              <div key={index} className="text-center flex-1">
                <div className="font-semibold text-gray-900">{item.range}</div>
                <div className="text-gray-400 text-xs">Followings</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Affinity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 w-full">
        {/* Brand Affinity */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Apple className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Brand Affinity
            </h3>
            <Tooltip
              description={
                "We determine brands the influencer mentioned by analyzing posts for caption texts, @mentions, #hashtags and location tags."
              }
            />
          </div>
          <div className="space-y-3 pl-2">
            {itemsToShow?.map((brand, index) => (
              <div key={index} className="flex items-center justify-between ">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Framer className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">
                    {brand.brand_name}
                  </span>
                </div>
                <span className="text-gray-500">
                  {(brand.weight * 100).toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Interest Affinity */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageCircleHeart className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Interest Affinity
            </h3>
            <Tooltip
              description={
                "We determine interests by analyzing posts for caption texts, @mentions, #hashtags and location tags."
              }
            />
          </div>

          <div className="space-y-3 pl-2">
            {brandAffinityInterest?.map((interest, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">
                    {interest.name}
                  </span>
                </div>
                <span className="text-gray-500">
                  {(interest.weight * 100).toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Followers */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-200 w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
              Top Followers
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          {followersData?.map((follower, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 hover:bg-green-50 rounded-xl transition-all duration-200"
            >
              {follower.profile_pic_url ? (
                <img
                  src={follower.profile_pic_url}
                  alt={follower.name}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-green-200"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 border-2 border-green-200 flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">
                    {follower.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-gray-900 font-semibold underline hover:text-green-600 transition-colors text-sm sm:text-base truncate">
                  @{follower.username}
                </div>
                <div className="text-gray-500 text-xs sm:text-sm truncate">
                  {follower.full_name}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="!flex !justify-end">
          <button className="!px-6 !py-6 sm:px-8 sm:py-4 !bg-gradient-to-r !from-green-500 !to-emerald-600 !text-white !rounded-xl !font-bold hover:!shadow-lg hover:!shadow-green-500/25 transition-all duration-300 !flex !items-center !gap-3 text-sm sm:text-base">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
            PROFILE INSIGHTS
          </button>
        </div>
      </div>
    </div>
  );
};
